// src/features/analytics/useAnalyticsAudience.ts

import { useQuery } from '@tanstack/react-query';
import { AnalyticsApi } from './AnalyticsApi';
import {
  ANALYTICS_QUERY_KEYS,
  type AnalyticsDimension,
  type AnalyticsMetric,
} from '@/lib/constants/analytics';
import type { UtcRange } from '@/shared/utils/utcRange';
import type { AnalyticsEnvelope, AudienceData } from './types';

export interface UseAnalyticsAudienceResult {
  audience: AnalyticsEnvelope<AudienceData> | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/** Audience dimension breakdown — account scope only. Pass null cid to disable. */
export function useAnalyticsAudience(
  cid: number | null,
  dimension: AnalyticsDimension,
  range: UtcRange,
  metric: AnalyticsMetric
): UseAnalyticsAudienceResult {
  const query = useQuery({
    queryKey: ANALYTICS_QUERY_KEYS.audience(cid ?? 0, range.from, range.to, dimension, metric),
    queryFn: () => AnalyticsApi.getAudience(cid!, dimension, range, metric),
    enabled: cid !== null,
    staleTime: 60_000,
  });

  return {
    audience: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
